import dayjs from "dayjs";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import ComplaintForm from "../components/ComplaintForm";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { categories } from "../utils/static-data";

const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

const ComplaintList = () => {
    const { user, loading: isLoading, users } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);

    // Status options
    const statusOptions = ["in-operation", "completed", "paused"];

    const [isFormVisible, setIsFormVisible] = useState(null);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [completionComment, setCompletionComment] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null); // For mobile expanded view

    const technicians = users.filter((u) => u.role === "technician");

    // Load complaints with real-time updates
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                let allComplaints = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Status filter
                allComplaints = allComplaints.filter(
                    (c) => c.status !== "completed" && c.status !== "paused"
                );

                // Role-based filtering
                if (user?.role === "technician") {
                    allComplaints = allComplaints.filter(
                        (c) => c.assignedTo === user.uid
                    );
                } else if (user?.role === "helpdesk") {
                    allComplaints = allComplaints.filter(
                        (c) => c.createdBy === user.uid
                    );
                }

                // ✅ Category filtering
                if (selectedCategory) {
                    allComplaints = allComplaints.filter(
                        (c) => c.category === selectedCategory
                    );
                }

                // Sorting
                allComplaints.sort(
                    (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                );

                setComplaints(allComplaints);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user, selectedCategory]);

    // Assign technician
    const handleAssignTechnician = async (complaintId, technicianId) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                assignedTo: technicianId,
                status: "in-operation",
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `Assigned to technician (ID: ${technicianId})`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("Technician assigned & status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to assign technician");
        }
    };

    // Change status
    const handleStatusChange = (complaintId, newStatus) => {
        if (
            user.role !== "admin" &&
            user.role !== "helpdesk" &&
            user.role !== "supervisor"
        ) {
            return toast.error("You do not have permission for this action");
        }

        const complaint = complaints.find((c) => c.id === complaintId);

        if (newStatus === "completed") {
            setSelectedComplaint(complaint);
            setShowCommentModal(true);
            return;
        }

        updateComplaintStatus(complaint, newStatus);
    };

    const updateComplaintStatus = async (complaint, newStatus, comment = "") => {
        try {
            const complaintRef = doc(db, "complaints", complaint.id);
            await updateDoc(complaintRef, {
                status: newStatus,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message:
                            newStatus === "completed"
                                ? `Status changed to completed. Comment: ${comment}`
                                : `Status changed to ${newStatus}`,
                        userId: user.uid,
                    },
                ],
                ...(newStatus === "completed" && { completionComment: comment, resolvedAt: new Date() }),
            });
            toast.success("Status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };

    const handleTimeChange = async (complaintId, newTime) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                approximateResolveTime: newTime,
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: new Date(),
                        message: `approximateResolveTime changed to ${newTime}`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("approximateResolveTime updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update approximateResolveTime");
        }
    }

    // Status badge component (for display only)
    const StatusBadge = ({ status }) => {
        const statusColors = {
            'in-operation': 'bg-blue-100 text-blue-800',
            'completed': 'bg-green-100 text-green-800',
            'paused': 'bg-yellow-100 text-yellow-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    // Status indicator for compact view
    const StatusIndicator = ({ status }) => {
        const statusColors = {
            'in-operation': 'bg-blue-500',
            'completed': 'bg-green-500',
            'paused': 'bg-yellow-500'
        };

        return (
            <div className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-500'}`}></span>
                <span className="text-xs capitalize">{status}</span>
            </div>
        );
    };

    if (loading || isLoading) return <Loading />;


    return (
        <>
            {isFormVisible && (
                <ComplaintForm
                    onClose={() => setIsFormVisible(false)}
                />
            )}

            {!complaints.length ? (
                // Empty State
                !isFormVisible && (
                    <div className="flex flex-col items-center justify-center h-screen gap-4 px-4">
                        <div className="flex flex-col gap-5">
                            <h2 className="text-xl sm:text-2xl font-semibold text-center">
                                No active complaints found
                            </h2>

                            {/* Create Complaint Button */}
                            {!selectedCategory && user && (user.role === "admin" || user.role === "helpdesk" || user.role === "supervisor") && (
                                <button
                                    className="bg-pink-400 text-gray-100 py-2 px-4 rounded-md hover:bg-pink-500 transition whitespace-nowrap"
                                    onClick={() => setIsFormVisible(true)}
                                >
                                    Create Complaint
                                </button>
                            )}

                            {/* Clear Filter Button */}
                            {selectedCategory && (
                                <button
                                    onClick={() => setSelectedCategory("")}
                                    className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition"
                                >
                                    Clear Filter
                                </button>
                            )}
                        </div>
                    </div>
                )
            ) : (
                // Main Content with Complaints
                !isFormVisible && (
                    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 space-y-6">
                        {/* Header Section */}
                        <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <h2 className="text-lg sm:text-xl font-semibold">Active Complaints ({complaints.length})</h2>

                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="w-full sm:w-48">
                                        <CustomSelect
                                            name="category"
                                            label=""
                                            options={categories}
                                            value={selectedCategory}
                                            placeholder="Filter by Category"
                                            onChange={(name, value) => {
                                                setSelectedCategory(value);
                                            }}
                                        />
                                    </div>

                                    {selectedCategory && (
                                        <button
                                            onClick={() => setSelectedCategory("")}
                                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition whitespace-nowrap"
                                        >
                                            Clear Filter
                                        </button>
                                    )}
                                </div>
                            </div>

                            <button
                                className="bg-pink-400 text-gray-100 py-2 px-4 rounded-md hover:bg-pink-500 transition whitespace-nowrap"
                                onClick={() => {
                                    if (
                                        user &&
                                        (user.role === "admin" ||
                                            user.role === "helpdesk" ||
                                            user.role === "supervisor")
                                    ) {
                                        setIsFormVisible(true);
                                    } else {
                                        return toast.error("You do not have permission for this action");
                                    }
                                }}
                            >
                                + New Complaint
                            </button>
                        </div>

                        {/* Desktop Table View - Hidden on mobile */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full table-auto border-collapse border border-gray-300 text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border p-2 text-center whitespace-nowrap">Date</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Ticket</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Customer</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Phone</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Category</th>
                                        <th className="border p-2 text-center whitespace-nowrap">ONU Status</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Router Status</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Est. Time</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Assigned To</th>
                                        <th className="border p-2 text-center whitespace-nowrap">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {complaints.map((c) => (
                                        <tr key={c.id} className="hover:bg-gray-50">
                                            <td className="border p-2 text-center whitespace-nowrap">{dayjs(c.createdAt.toDate()).format("DD-MM-YY")}</td>
                                            <td className="border p-2 text-center whitespace-nowrap font-mono text-xs">{c.ticketNumber}</td>
                                            <td className="border p-2 text-center" title={c.customerInfo?.name}>
                                                {c.customerInfo?.name.length > 15 ?
                                                    `${c.customerInfo?.name.slice(0, 14)}...` :
                                                    c.customerInfo?.name}
                                            </td>
                                            <td className="border p-2 text-center whitespace-nowrap">{c.customerInfo?.phone}</td>
                                            <td className="border p-2 text-center whitespace-nowrap">{c?.category || "-"}</td>

                                            {/* ONU Status - Compact Display */}
                                            <td className="border p-2 w-20">
                                                {c.category === "Red signal" ? (
                                                    <div className="flex flex-col gap-1 text-xs">
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">Power:</span>
                                                            <span className={`${c.onu?.power === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.onu?.power || '-'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">PON:</span>
                                                            <span className={`${c.onu?.pon === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.onu?.pon || '-'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">LAN:</span>
                                                            <span className={`${c.onu?.lan === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.onu?.lan || '-'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : <p className="text-center">-</p>}
                                            </td>

                                            {/* Router Status - Compact Display */}
                                            <td className="border p-2 w-20">
                                                {c.category === "Red signal" ? (
                                                    <div className="flex flex-col gap-1 text-xs">
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">Power:</span>
                                                            <span className={`${c.router?.power === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.router?.power || '-'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">WLAN:</span>
                                                            <span className={`${c.router?.wanLan === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.router?.wanLan || '-'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-semibold w-10">WIFI:</span>
                                                            <span className={`${c.router?.wifi === 'ON' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {c.router?.wifi || '-'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : <p className="text-center">-</p>}

                                            </td>

                                            <td className="border p-2">
                                                <CustomSelect
                                                    name="approximateResolveTime"
                                                    placeholder="Select"
                                                    options={resolveTimeOptions}
                                                    value={c.approximateResolveTime}
                                                    onChange={(name, value) => handleTimeChange(c.id, value)}
                                                    className="text-xs"
                                                />
                                            </td>
                                            <td className="border p-2 w-10">
                                                <select
                                                    value={c.assignedTo || ""}
                                                    onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-1 focus:ring-gray-400 focus:outline-none"
                                                    title={technicians.find(t => t.uid === c.assignedTo)?.name}
                                                >
                                                    <option value="">Assign</option>
                                                    {technicians.map((tech) => (
                                                        <option key={tech.uid} value={tech.uid}>
                                                            {tech.name} ({tech.department})
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="border p-2 w-20">
                                                {/* FIXED: Changed from StatusBadge to CustomSelect for status updates */}
                                                <CustomSelect
                                                    name="status"
                                                    placeholder="Change status"
                                                    options={statusOptions}
                                                    value={c.status}
                                                    onChange={(name, value) => handleStatusChange(c.id, value)}
                                                    className="text-xs"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-4">
                            {complaints.map((c) => (
                                <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                                    {/* Card Header - Always Visible */}
                                    <div
                                        className="p-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                                        onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs bg-gray-200 px-2 py-1 rounded">
                                                    {c.ticketNumber}
                                                </span>
                                                <StatusIndicator status={c.status} />
                                            </div>
                                            <div className="mt-1">
                                                <div className="font-medium">{c.customerInfo?.name}</div>
                                                <div className="text-xs text-gray-600">{c.customerInfo?.phone}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-gray-500">
                                                {dayjs(c.createdAt.toDate()).format("DD/MM/YY")}
                                            </div>
                                            <div className="text-xs font-medium mt-1">{c.category || '-'}</div>
                                            <svg
                                                className={`w-5 h-5 mt-1 mx-auto transition-transform ${expandedRow === c.id ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Expanded Content */}
                                    {expandedRow === c.id && (
                                        <div className="p-3 space-y-3">
                                            {/* ONU Status */}
                                            <div className="bg-blue-50 p-2 rounded">
                                                <div className="font-medium text-sm mb-1">ONU Status</div>
                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                    <div>
                                                        <span className="text-gray-600">Power:</span>{' '}
                                                        <span className={c.onu?.power === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.onu?.power || '-'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">PON:</span>{' '}
                                                        <span className={c.onu?.pon === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.onu?.pon || '-'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">LAN:</span>{' '}
                                                        <span className={c.onu?.lan === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.onu?.lan || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Router Status */}
                                            <div className="bg-purple-50 p-2 rounded">
                                                <div className="font-medium text-sm mb-1">Router Status</div>
                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                    <div>
                                                        <span className="text-gray-600">Power:</span>{' '}
                                                        <span className={c.router?.power === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.router?.power || '-'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">WLAN:</span>{' '}
                                                        <span className={c.router?.wanLan === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.router?.wanLan || '-'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">WIFI:</span>{' '}
                                                        <span className={c.router?.wifi === 'ON' ? 'text-green-600 font-medium' : 'text-red-600'}>
                                                            {c.router?.wifi || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="space-y-2">
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Est. Time</label>
                                                    <CustomSelect
                                                        name="approximateResolveTime"
                                                        placeholder="Select time"
                                                        options={resolveTimeOptions}
                                                        value={c.approximateResolveTime}
                                                        onChange={(name, value) => handleTimeChange(c.id, value)}
                                                        className="text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Assign Technician</label>
                                                    <select
                                                        value={c.assignedTo || ""}
                                                        onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-gray-400 focus:outline-none"
                                                    >
                                                        <option value="">Select technician</option>
                                                        {technicians.map((tech) => (
                                                            <option key={tech.uid} value={tech.uid}>
                                                                {tech.name} ({tech.department})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Status</label>
                                                    <CustomSelect
                                                        name="status"
                                                        placeholder="Change status"
                                                        options={statusOptions}
                                                        value={c.status}
                                                        onChange={(name, value) => handleStatusChange(c.id, value)}
                                                        className="text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Comment Modal */}
                        {showCommentModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
                                <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                                    <h3 className="text-lg font-semibold mb-4 text-center">
                                        Add Completion Comment
                                    </h3>
                                    <textarea
                                        value={completionComment}
                                        onChange={(e) => setCompletionComment(e.target.value)}
                                        placeholder="Enter a comment about how the task was completed..."
                                        className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring focus:ring-gray-300"
                                        rows={4}
                                    />
                                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                                        <button
                                            onClick={() => {
                                                setShowCommentModal(false);
                                                setCompletionComment("");
                                            }}
                                            className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 order-2 sm:order-1"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (!completionComment.trim()) {
                                                    toast.error("Please add a comment before completing.");
                                                    return;
                                                }
                                                updateComplaintStatus(selectedComplaint, "completed", completionComment);
                                                setShowCommentModal(false);
                                                setCompletionComment("");
                                                setSelectedComplaint(null);
                                            }}
                                            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 order-1 sm:order-2 mb-2 sm:mb-0"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
        </>
    );
};

export default ComplaintList;


// import dayjs from "dayjs";
// import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
// import { useEffect, useState } from "react";
// import toast from "react-hot-toast";
// import CustomSelect from "../components/common/CustomSelect";
// import { Loading } from "../components/common/Loading";
// import ComplaintForm from "../components/ComplaintForm";
// import { useAuth } from "../hooks/useAuth";
// import { db } from "../network/firebaseConfig";
// import { categories } from "../utils/static-data";
// const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

// const ComplaintList = () => {
//     const { user, loading: isLoading, users } = useAuth();
//     const [complaints, setComplaints] = useState([]);
//     const [loading, setLoading] = useState(true);

//     // Status options
//     const statusOptions = ["in-operation", "completed", "paused"];

//     const [isFormVisible, setIsFormVisible] = useState(null); // 👈 new
//     // const [technicians, setTechnicians] = useState([]);

//     const [showCommentModal, setShowCommentModal] = useState(false);
//     const [selectedComplaint, setSelectedComplaint] = useState(null);
//     const [completionComment, setCompletionComment] = useState("");

//     const [selectedCategory, setSelectedCategory] = useState(null);

//     const technicians = users.filter((u) => u.role === "technician");

//     // Load complaints with real-time updates
//     useEffect(() => {
//         const unsubscribe = onSnapshot(
//             collection(db, "complaints"),
//             (snapshot) => {
//                 let allComplaints = snapshot.docs.map((doc) => ({
//                     id: doc.id,
//                     ...doc.data(),
//                 }));

//                 // Status filter
//                 allComplaints = allComplaints.filter(
//                     (c) => c.status !== "completed" && c.status !== "paused"
//                 );

//                 // Role-based filtering
//                 if (user?.role === "technician") {
//                     allComplaints = allComplaints.filter(
//                         (c) => c.assignedTo === user.uid
//                     );
//                 } else if (user?.role === "helpdesk") {
//                     allComplaints = allComplaints.filter(
//                         (c) => c.createdBy === user.uid
//                     );
//                 }

//                 // ✅ Category filtering
//                 if (selectedCategory) {
//                     allComplaints = allComplaints.filter(
//                         (c) => c.category === selectedCategory
//                     );
//                 }

//                 // Sorting
//                 allComplaints.sort(
//                     (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
//                 );

//                 setComplaints(allComplaints);
//                 setLoading(false);
//             },
//             (err) => {
//                 console.error(err);
//                 toast.error("Failed to load complaints");
//                 setLoading(false);
//             }
//         );

//         return () => unsubscribe();
//     }, [user, selectedCategory]);


//     // Assign technician
//     const handleAssignTechnician = async (complaintId, technicianId) => {
//         if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
//             return toast.error("You do not have permission for this action");
//         }
//         try {
//             const complaintRef = doc(db, "complaints", complaintId);
//             const complaint = complaints.find((c) => c.id === complaintId);
//             await updateDoc(complaintRef, {
//                 assignedTo: technicianId,
//                 status: "in-operation",
//                 activityLogs: [
//                     ...(complaint.activityLogs || []),
//                     {
//                         time: new Date(),
//                         message: `Assigned to technician (ID: ${technicianId})`,
//                         userId: user.uid,
//                     },
//                 ],
//             });
//             toast.success("Technician assigned & status updated!");
//         } catch (err) {
//             console.error(err);
//             toast.error("Failed to assign technician");
//         }
//     };

//     // Change status
//     const handleStatusChange = (complaintId, newStatus) => {
//         if (
//             user.role !== "admin" &&
//             user.role !== "helpdesk" &&
//             user.role !== "supervisor"
//         ) {
//             return toast.error("You do not have permission for this action");
//         }

//         const complaint = complaints.find((c) => c.id === complaintId);

//         if (newStatus === "completed") {
//             setSelectedComplaint(complaint);
//             setShowCommentModal(true);
//             return; // 🛑 stop here; we’ll update after comment submission
//         }

//         // Normal update for other statuses
//         updateComplaintStatus(complaint, newStatus);
//     };


//     const updateComplaintStatus = async (complaint, newStatus, comment = "") => {
//         try {
//             const complaintRef = doc(db, "complaints", complaint.id);
//             await updateDoc(complaintRef, {
//                 status: newStatus,
//                 activityLogs: [
//                     ...(complaint.activityLogs || []),
//                     {
//                         time: new Date(),
//                         message:
//                             newStatus === "completed"
//                                 ? `Status changed to completed. Comment: ${comment}`
//                                 : `Status changed to ${newStatus}`,
//                         userId: user.uid,
//                     },
//                 ],
//                 ...(newStatus === "completed" && { completionComment: comment, resolvedAt: new Date() }), // optional: store in root
//             });
//             toast.success("Status updated!");
//         } catch (err) {
//             console.error(err);
//             toast.error("Failed to update status");
//         }
//     };


//     const handleTimeChange = async (complaintId, newTime) => {
//         if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
//             return toast.error("You do not have permission for this action");
//         }
//         try {
//             const complaintRef = doc(db, "complaints", complaintId);
//             const complaint = complaints.find((c) => c.id === complaintId);
//             await updateDoc(complaintRef, {
//                 approximateResolveTime: newTime,
//                 activityLogs: [
//                     ...(complaint.activityLogs || []),
//                     {
//                         time: new Date(),
//                         message: `approximateResolveTime changed to ${newTime}`,
//                         userId: user.uid,
//                     },
//                 ],
//             });
//             toast.success("approximateResolveTime updated!");
//         } catch (err) {
//             console.error(err);
//             toast.error("Failed to update approximateResolveTime");
//         }
//     }

//     if (loading || isLoading) return <Loading />;

//     if (!complaints.length) {
//         return (
//             <div className="flex flex-col items-center justify-center h-screen gap-4">
//                 <h2 className="text-2xl font-semibold">
//                     No active complaints found
//                 </h2>

//                 {selectedCategory && (
//                     <button
//                         onClick={() => setSelectedCategory("")}
//                         className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition"
//                     >
//                         Clear Filter
//                     </button>
//                 )}
//             </div>
//         );
//     }

//     return (
//         <>

//             {isFormVisible && (
//                 <ComplaintForm
//                     onClose={() => setIsFormVisible(false)}
//                 />
//             )}
//             {!isFormVisible && (
//                 <div className="max-w-6xl mx-auto p-0 space-y-6">
//                     <div className="flex flex-row justify-between">
//                         <div className="flex justify-left gap-2 w-[50%] items-center">
//                             <h2 className="text-xl font-semibold w-[50%]">Active Complaints</h2>

//                             <CustomSelect
//                                 name="category"
//                                 label="Category"
//                                 options={categories}
//                                 value={selectedCategory}
//                                 placeholder="Select Category"
//                                 onChange={(name, value) => {
//                                     setSelectedCategory(value);
//                                 }}
//                             />

//                             {/* ✅ Clear button */}
//                             {selectedCategory && (
//                                 <button
//                                     onClick={() => setSelectedCategory("")}
//                                     className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
//                                 >
//                                     Clear
//                                 </button>
//                             )}
//                         </div>

//                         <button
//                             className="bg-pink-400 text-gray-100 py-1 px-2 rounded-md"
//                             onClick={() => {
//                                 if (
//                                     user &&
//                                     (user.role === "admin" ||
//                                         user.role === "helpdesk" ||
//                                         user.role === "supervisor")
//                                 ) {
//                                     setIsFormVisible(true);
//                                 } else {
//                                     return toast.error("You do not have permission for this action");
//                                 }
//                             }}
//                         >
//                             + Record Complaint
//                         </button>
//                     </div>

//                     <table className="w-full table-auto border-collapse border border-gray-300">
//                         <thead>
//                             <tr className="bg-gray-100">
//                                 <th className="border p-1 text-center">C.. Date</th>
//                                 <th className="border p-1 text-center">Ticket</th>
//                                 <th className="border p-1 text-center">Customer</th>
//                                 <th className="border p-1 text-center">Phone</th>
//                                 <th className="border p-1 text-center">Category</th>
//                                 <th className="border p-1 text-center">ONU Status</th>
//                                 <th className="border p-1 text-center">Router Status</th>
//                                 <th className="border p-1 text-center">Approx.Time</th>
//                                 <th className="border p-1 text-center">Assigned To</th>
//                                 <th className="border p-1 text-center">Status</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {complaints.map((c) => (
//                                 <tr key={c.id} className="hover:bg-gray-50">
//                                     <td className="border p-1 text-center w-20">{dayjs(c.createdAt.toDate()).format("DD-MM-YY")}</td>
//                                     <td className="border p-1 text-center w-24">{c.ticketNumber}</td>

//                                     {/* when user hover on the customer name it should show customer details from backend (id, name, address, mobile etc) */}
//                                     <td className="border p-1 text-center">{
//                                         c.customerInfo?.name.length > 8 ?
//                                             `${c.customerInfo?.name.slice(0, 7)}...` : c.customerInfo?.name}</td>
//                                     <td className="border p-1 text-center">{c.customerInfo?.phone}</td>
//                                     <td className="border p-1 text-center">{c?.category || "-"}</td>

//                                     <td className="border p-1">
//                                         <div className="flex flex-col">
//                                             <span>Power: {c.onu?.power}</span>
//                                             <span>PON: {c.onu?.pon}</span>
//                                             <span>LAN: {c.onu?.lan}</span>
//                                         </div>
//                                     </td>
//                                     <td className="border p-1">
//                                         <div className="flex flex-col">
//                                             <span>Power: {c.router?.power}</span>
//                                             <span>WLAN: {c.router?.wanLan}</span>
//                                             <span>WIFI: {c.router?.wifi}</span>
//                                         </div>
//                                     </td>
//                                     <td className="border p-1 text-center">
//                                         <CustomSelect
//                                             name="approximateResolveTime"
//                                             placeholder="Select Time"
//                                             options={resolveTimeOptions}
//                                             value={c.approximateResolveTime}
//                                             onChange={(name, value) => handleTimeChange(c.id, value)}
//                                         />
//                                     </td>
//                                     <td className="border p-1 w-40">
//                                         <select
//                                             value={c.assignedTo || ""}
//                                             onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
//                                             className="w-full border border-gray-300 rounded-md px-2 py-1 focus:ring-1 focus:ring-gray-400 focus:outline-none"
//                                         >
//                                             <option value="">Assign Technician</option>
//                                             {technicians.map((tech) => (
//                                                 <option key={tech.uid} value={tech.uid}>
//                                                     {tech.name} ({tech.department})
//                                                 </option>
//                                             ))}
//                                         </select>
//                                     </td>


//                                     <td className="border p-1 w-32">
//                                         <CustomSelect
//                                             name="status"
//                                             placeholder="Status"
//                                             options={statusOptions}
//                                             value={c.status}
//                                             onChange={(name, value) => handleStatusChange(c.id, value)}
//                                         />
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>

//                     {showCommentModal && (
//                         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//                             <div className="bg-white p-6 rounded-lg shadow-lg w-96">
//                                 <h3 className="text-lg font-semibold mb-4 text-center">
//                                     Add Completion Comment
//                                 </h3>
//                                 <textarea
//                                     value={completionComment}
//                                     onChange={(e) => setCompletionComment(e.target.value)}
//                                     placeholder="Enter a comment about how the task was completed..."
//                                     className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring focus:ring-gray-300"
//                                     rows={4}
//                                 />
//                                 <div className="flex justify-end gap-2">
//                                     <button
//                                         onClick={() => {
//                                             setShowCommentModal(false);
//                                             setCompletionComment("");
//                                         }}
//                                         className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
//                                     >
//                                         Cancel
//                                     </button>
//                                     <button
//                                         onClick={() => {
//                                             if (!completionComment.trim()) {
//                                                 toast.error("Please add a comment before completing.");
//                                                 return;
//                                             }
//                                             updateComplaintStatus(selectedComplaint, "completed", completionComment);
//                                             setShowCommentModal(false);
//                                             setCompletionComment("");
//                                             setSelectedComplaint(null);
//                                         }}
//                                         className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
//                                     >
//                                         Submit
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     )}

//                 </div>
//             )}
//         </>
//     );
// };

// export default ComplaintList;
